import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    TrendingUp, 
    TrendingDown,
    AlertTriangle,
    CheckCircle,
    Target,
    Loader2,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    MapPin,
    DollarSign,
    Clock,
    Building2,
    Truck,
    ArrowRight,
    BarChart3,
    Brain,
    Zap,
    Info,
    PhoneCall,
    ExternalLink,
    CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import CallAssistPanel from './CallAssistPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority badge styles
const getPriorityBadge = (priority, score) => {
    if (priority === 'PRIORITY') {
        return (
            <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/50">
                <Target className="w-3 h-3 mr-1" />
                PRIORITY {score}/100
            </Badge>
        );
    } else if (priority === 'CONSIDER') {
        return (
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/50">
                <AlertTriangle className="w-3 h-3 mr-1" />
                CONSIDER {score}/100
            </Badge>
        );
    } else {
        return (
            <Badge className="bg-red-500/20 text-red-400 border border-red-500/50">
                <TrendingDown className="w-3 h-3 mr-1" />
                AVOID {score}/100
            </Badge>
        );
    }
};

// Decision badge
const getDecisionBadge = (decision) => {
    if (decision === 'BOOK') {
        return <Badge className="bg-green-600 text-white">BOOK</Badge>;
    } else if (decision === 'NEGOTIATE') {
        return <Badge className="bg-amber-600 text-white">NEGOTIATE</Badge>;
    } else {
        return <Badge className="bg-red-600 text-white">PASS</Badge>;
    }
};

// Score component breakdown card
function ScoreBreakdown({ components }) {
    return (
        <div className="space-y-2">
            {components.map((comp, idx) => (
                <div key={idx} className="flex items-center gap-2">
                    <div className="w-28 text-xs text-zinc-400">{comp.name}</div>
                    <div className="flex-1">
                        <Progress 
                            value={comp.score} 
                            className={`h-2 ${
                                comp.impact === 'positive' ? '[&>div]:bg-green-500' :
                                comp.impact === 'negative' ? '[&>div]:bg-red-500' :
                                '[&>div]:bg-zinc-500'
                            }`}
                        />
                    </div>
                    <div className={`text-xs w-12 text-right ${
                        comp.impact === 'positive' ? 'text-green-400' :
                        comp.impact === 'negative' ? 'text-red-400' :
                        'text-zinc-400'
                    }`}>
                        {comp.score.toFixed(0)}/100
                    </div>
                </div>
            ))}
        </div>
    );
}

// Truck Selection Card - Enhanced for quick decisions
function TruckCard({ truck, isSelected, onSelect }) {
    const statusColors = {
        'available': 'bg-green-500',
        'assigned': 'bg-blue-500',
        'in_transit': 'bg-amber-500',
        'offline': 'bg-zinc-500'
    };
    
    const noGoStates = truck.no_go_states || [];
    
    return (
        <button
            onClick={() => onSelect(truck.truck_id)}
            className={`p-3 rounded-lg border-2 transition-all text-left min-w-[160px] flex-shrink-0 ${
                isSelected 
                    ? 'bg-purple-500/20 border-purple-500 ring-2 ring-purple-500/50 shadow-lg shadow-purple-500/20' 
                    : 'bg-zinc-800/50 border-zinc-700 hover:border-purple-500/50 hover:bg-zinc-800'
            }`}
            data-testid={`truck-card-${truck.truck_id}`}
        >
            {/* Truck Name - Large and Clear */}
            <div className="flex items-center gap-2 mb-2">
                <Truck className={`w-5 h-5 ${isSelected ? 'text-purple-400' : 'text-zinc-400'}`} />
                <span className={`font-bold ${isSelected ? 'text-white text-base' : 'text-zinc-200 text-sm'}`}>
                    {truck.name}
                </span>
                {isSelected && <CheckCircle2 className="w-5 h-5 text-purple-400 ml-auto" />}
            </div>
            
            {/* Location - PROMINENT */}
            {truck.location ? (
                <div className={`flex items-center gap-1 text-sm mb-2 ${isSelected ? 'text-cyan-400 font-medium' : 'text-zinc-300'}`}>
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{truck.location}</span>
                </div>
            ) : (
                <div className="flex items-center gap-1 text-sm mb-2 text-zinc-500">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>Location unknown</span>
                </div>
            )}
            
            {/* Status & Equipment Row */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs">
                    <span className={`w-2.5 h-2.5 rounded-full ${statusColors[truck.status] || statusColors.offline}`}></span>
                    <span className={`capitalize ${isSelected ? 'text-zinc-200' : 'text-zinc-400'}`}>
                        {truck.status?.replace('_', ' ') || 'Available'}
                    </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${isSelected ? 'bg-purple-500/30 text-purple-300' : 'bg-zinc-700 text-zinc-400'}`}>
                    {truck.equipment_type || 'Van'}
                </span>
            </div>
            
            {/* No-Go States Badge - Minimal */}
            {noGoStates.length > 0 && (
                <div className="mt-2 text-[10px] text-red-400/80 flex items-center gap-1">
                    <span>🚫</span>
                    <span>{noGoStates.join(', ')}</span>
                </div>
            )}
        </button>
    );
}

// Individual load card with priority info - Enhanced with deadhead display
function PriorityLoadCard({ load, onStage }) {
    const [expanded, setExpanded] = useState(false);
    const [showCallAssist, setShowCallAssist] = useState(false);
    const navigate = useNavigate();
    const ps = load.priority_score || {};
    
    // Extract deadhead from scoring components
    const deadheadComponent = ps.components?.find(c => c.name === 'Deadhead Distance');
    const deadheadExplanation = deadheadComponent?.explanation || '';
    const deadheadMatch = deadheadExplanation.match(/(\d+(?:\.\d+)?)\s*mi/);
    const deadheadMiles = deadheadMatch ? parseFloat(deadheadMatch[1]) : (load.original_deadhead_miles || load.deadhead_miles || 0);
    
    const bgColor = ps.priority === 'PRIORITY' ? 'border-l-blue-500' :
                    ps.priority === 'CONSIDER' ? 'border-l-amber-500' : 'border-l-red-500';
    
    const ratePerMile = ((load.gross_pay || 0) / (load.distance || 1)).toFixed(2);
    
    return (
        <Card className={`bg-zinc-900/50 border-zinc-800 border-l-4 ${bgColor}`}>
            <CardContent className="p-4">
                {/* Header Row */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            {getPriorityBadge(ps.priority, ps.total_score)}
                            {getDecisionBadge(ps.decision)}
                            <span 
                                className="text-zinc-500 text-xs font-mono cursor-pointer hover:text-cyan-400"
                                onClick={() => navigate(`/loads/${load.load_id}`)}
                            >
                                {load.load_id}
                                <ExternalLink className="w-3 h-3 inline ml-1" />
                            </span>
                        </div>
                        
                        {/* Route */}
                        <div className="flex items-center gap-2 text-white">
                            <MapPin className="w-4 h-4 text-green-400" />
                            <span>{load.origin_city}, {load.origin_state}</span>
                            <ArrowRight className="w-4 h-4 text-zinc-500" />
                            <MapPin className="w-4 h-4 text-blue-400" />
                            <span>{load.destination_city}, {load.destination_state}</span>
                        </div>
                        
                        {/* Key Stats - Compact 4-column grid */}
                        <div className="flex items-center gap-1 mt-3 p-2 bg-zinc-800/50 rounded-lg text-center">
                            {/* Total Rate */}
                            <div className="flex-1 border-r border-zinc-700">
                                <p className="text-green-400 font-bold">${(load.gross_pay || 0).toLocaleString()}</p>
                                <p className="text-[10px] text-zinc-500">Rate</p>
                            </div>
                            {/* Rate Per Mile */}
                            <div className="flex-1 border-r border-zinc-700">
                                <p className="text-cyan-400 font-bold">${ratePerMile}</p>
                                <p className="text-[10px] text-zinc-500">/Mi</p>
                            </div>
                            {/* Distance */}
                            <div className="flex-1 border-r border-zinc-700">
                                <p className="text-white font-bold">{load.distance || 0}</p>
                                <p className="text-[10px] text-zinc-500">Mi</p>
                            </div>
                            {/* Deadhead - Color coded */}
                            <div className="flex-1">
                                <p className={`font-bold ${deadheadMiles > 100 ? 'text-red-400' : deadheadMiles > 50 ? 'text-amber-400' : 'text-green-400'}`}>
                                    {deadheadMiles.toFixed(0)}
                                </p>
                                <p className="text-[10px] text-zinc-500">DH</p>
                            </div>
                        </div>
                        
                        {/* Broker */}
                        {load.broker_name && (
                            <div className="flex items-center gap-2 mt-2 text-sm text-zinc-400">
                                <Building2 className="w-4 h-4" />
                                <span>{load.broker_name}</span>
                            </div>
                        )}
                        
                        {/* Summary */}
                        <p className={`text-sm mt-2 ${
                            ps.priority === 'PRIORITY' ? 'text-blue-400' :
                            ps.priority === 'CONSIDER' ? 'text-amber-400' : 'text-red-400'
                        }`}>
                            {ps.summary}
                        </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                        <Button 
                            size="sm"
                            onClick={() => setShowCallAssist(!showCallAssist)}
                            className={showCallAssist ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-600 hover:bg-cyan-700'}
                            data-testid={`call-assist-${load.load_id}`}
                        >
                            <PhoneCall className="w-4 h-4 mr-1" />
                            {showCallAssist ? 'Close' : 'Call'}
                        </Button>
                        <Button 
                            size="sm"
                            onClick={() => onStage?.(load.load_id)}
                            className={ps.priority === 'PRIORITY' 
                                ? 'bg-blue-600 hover:bg-blue-700' 
                                : 'bg-zinc-700 hover:bg-zinc-600'
                            }
                            data-testid={`stage-priority-${load.load_id}`}
                        >
                            Stage Load
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpanded(!expanded)}
                            className="text-zinc-400"
                        >
                            {expanded ? (
                                <>Details <ChevronUp className="w-4 h-4 ml-1" /></>
                            ) : (
                                <>Details <ChevronDown className="w-4 h-4 ml-1" /></>
                            )}
                        </Button>
                    </div>
                </div>
                
                {/* Expanded Details */}
                {expanded && (
                    <div className="mt-4 pt-4 border-t border-zinc-700 space-y-4">
                        {/* Score Breakdown */}
                        <div>
                            <h4 className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-purple-400" />
                                Score Breakdown
                            </h4>
                            <ScoreBreakdown components={ps.components || []} />
                        </div>
                        
                        {/* Key Factors */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Reasons */}
                            {ps.key_reasons?.length > 0 && (
                                <div>
                                    <h4 className="text-xs text-green-400 mb-2">WHY BOOK:</h4>
                                    {ps.key_reasons.map((reason, idx) => (
                                        <p key={idx} className="text-xs text-zinc-300 flex items-start gap-1">
                                            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                            {reason}
                                        </p>
                                    ))}
                                </div>
                            )}
                            
                            {/* Risks */}
                            {ps.key_risks?.length > 0 && (
                                <div>
                                    <h4 className="text-xs text-red-400 mb-2">RISKS:</h4>
                                    {ps.key_risks.map((risk, idx) => (
                                        <p key={idx} className="text-xs text-zinc-300 flex items-start gap-1">
                                            <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                                            {risk}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Profit Info */}
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-zinc-400">
                                Green Line: ${ps.green_line?.toLocaleString() || '—'}
                            </span>
                            <span className={ps.profit_margin >= 0 ? 'text-green-400' : 'text-red-400'}>
                                Margin: ${ps.profit_margin?.toLocaleString() || '—'}
                            </span>
                        </div>
                    </div>
                )}
                
                {/* Inline Call Assist Panel */}
                {showCallAssist && (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                        <CallAssistPanel 
                            loadId={load.load_id}
                            onClose={() => setShowCallAssist(false)}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Main Priority Dashboard Component - Truck-Centric Design
export default function LoadPriorityDashboard({ onStageLoad }) {
    const [trucks, setTrucks] = useState([]);
    const [selectedTruck, setSelectedTruck] = useState(null);
    const [data, setData] = useState(null);
    const [loadingTrucks, setLoadingTrucks] = useState(true);
    const [loadingLoads, setLoadingLoads] = useState(false);
    const [activeTab, setActiveTab] = useState('priority');
    
    // Fetch trucks for selection bar
    const fetchTrucks = useCallback(async () => {
        setLoadingTrucks(true);
        try {
            const res = await fetch(`${API_URL}/api/priority/trucks`, {
                credentials: 'include'
            });
            if (res.ok) {
                const result = await res.json();
                setTrucks(result.trucks || []);
            } else {
                toast.error('Failed to fetch trucks');
            }
        } catch (e) {
            console.error('Failed to fetch trucks:', e);
        } finally {
            setLoadingTrucks(false);
        }
    }, []);
    
    // Fetch prioritized loads for selected truck
    const fetchPrioritizedLoads = useCallback(async (truckId) => {
        if (!truckId) {
            setData(null);
            return;
        }
        
        setLoadingLoads(true);
        try {
            const res = await fetch(`${API_URL}/api/priority/loads?truck_id=${truckId}&limit=20`, {
                credentials: 'include'
            });
            if (res.ok) {
                const result = await res.json();
                setData(result);
            } else {
                toast.error('Failed to fetch prioritized loads');
            }
        } catch (e) {
            console.error('Failed to fetch prioritized loads:', e);
            toast.error('Failed to load priority data');
        } finally {
            setLoadingLoads(false);
        }
    }, []);
    
    // Initial load - fetch trucks
    useEffect(() => {
        fetchTrucks();
    }, [fetchTrucks]);
    
    // Handle truck selection
    const handleTruckSelect = (truckId) => {
        setSelectedTruck(truckId);
        setActiveTab('priority'); // Reset to priority tab
        fetchPrioritizedLoads(truckId);
    };
    
    const handleStage = async (loadId) => {
        if (!selectedTruck) {
            toast.error('Please select a truck first');
            return;
        }
        if (onStageLoad) {
            await onStageLoad(loadId, selectedTruck);
            // Refresh the loads after staging
            fetchPrioritizedLoads(selectedTruck);
        }
    };
    
    const stats = data?.stats || {};
    const tabs = [
        { key: 'priority', label: 'Priority', count: stats.priority_count || 0, color: 'text-blue-400', loads: data?.priority || [] },
        { key: 'consider', label: 'Consider', count: stats.consider_count || 0, color: 'text-amber-400', loads: data?.consider || [] },
        { key: 'avoid', label: 'Avoid', count: stats.avoid_count || 0, color: 'text-red-400', loads: data?.avoid || [] }
    ];
    
    const activeLoads = tabs.find(t => t.key === activeTab)?.loads || [];
    const selectedTruckData = trucks.find(t => t.truck_id === selectedTruck);
    
    return (
        <div className="space-y-4" data-testid="load-priority-dashboard">
            {/* Header */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Brain className="w-5 h-5 text-purple-500" />
                            Load Priority Ranking
                            <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">
                                Small Fleet Mode
                            </Badge>
                        </CardTitle>
                        {selectedTruck && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => fetchPrioritizedLoads(selectedTruck)}
                                disabled={loadingLoads}
                                className="text-zinc-400"
                            >
                                <RefreshCw className={`w-4 h-4 mr-1 ${loadingLoads ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                        )}
                    </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                    {/* Truck Selection Bar */}
                    <div>
                        <p className="text-sm text-zinc-400 mb-2 flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            Select a truck to view prioritized loads:
                        </p>
                        
                        {loadingTrucks ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                            </div>
                        ) : trucks.length === 0 ? (
                            <div className="text-center py-4 text-zinc-500">
                                <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p>No trucks found. Add trucks to get started.</p>
                            </div>
                        ) : (
                            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="truck-selection-bar">
                                {trucks.map(truck => (
                                    <TruckCard
                                        key={truck.truck_id}
                                        truck={truck}
                                        isSelected={selectedTruck === truck.truck_id}
                                        onSelect={handleTruckSelect}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Selected Truck Info & Stats */}
                    {selectedTruck && data && (
                        <>
                            {/* PROMINENT SELECTED TRUCK BANNER */}
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-purple-500/20 p-2 rounded-full">
                                            <Truck className="w-6 h-6 text-purple-400" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-white font-bold text-lg">{selectedTruckData?.name}</span>
                                                <Badge className="bg-purple-500/30 text-purple-300 border-purple-500/50">
                                                    SELECTED
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-zinc-400">
                                                {selectedTruckData?.location && (
                                                    <span className="flex items-center gap-1">
                                                        <MapPin className="w-3 h-3 text-cyan-400" />
                                                        {selectedTruckData.location}
                                                    </span>
                                                )}
                                                <span className="px-2 py-0.5 rounded bg-zinc-700 text-xs">
                                                    {selectedTruckData?.equipment_type || 'Van'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                                            <TrendingUp className="w-4 h-4" />
                                            Avg Score: <span className="text-white font-bold text-lg">{stats.average_score || 0}</span>/100
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Stats Summary */}
                            <div className="grid grid-cols-4 gap-3">
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-white">{stats.total_loads || 0}</p>
                                    <p className="text-xs text-zinc-500">Runnable</p>
                                </div>
                                <div className="bg-blue-500/10 rounded-lg p-3 text-center border border-blue-500/30">
                                    <p className="text-2xl font-bold text-blue-400">{stats.priority_count || 0}</p>
                                    <p className="text-xs text-blue-300">Book Now</p>
                                </div>
                                <div className="bg-amber-500/10 rounded-lg p-3 text-center border border-amber-500/30">
                                    <p className="text-2xl font-bold text-amber-400">{stats.consider_count || 0}</p>
                                    <p className="text-xs text-amber-300">Negotiate</p>
                                </div>
                                <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/30">
                                    <p className="text-2xl font-bold text-red-400">{stats.avoid_count || 0}</p>
                                    <p className="text-xs text-red-300">Pass</p>
                                </div>
                            </div>
                            
                            {/* Filter Summary - Compact display of all filters */}
                            {(stats.total_filtered > 0) && (
                                <div className="bg-zinc-800/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
                                        <Info className="w-3 h-3" />
                                        <span className="font-medium">{stats.total_filtered} loads filtered out:</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-xs">
                                        {stats.filtered_radius > 0 && (
                                            <span className="px-2 py-1 bg-zinc-700/50 rounded text-zinc-300">
                                                📍 {stats.filtered_radius} too far ({stats.truck_search_radius || 150}mi radius)
                                            </span>
                                        )}
                                        {stats.filtered_equipment > 0 && (
                                            <span className="px-2 py-1 bg-zinc-700/50 rounded text-zinc-300">
                                                🚛 {stats.filtered_equipment} wrong equipment
                                            </span>
                                        )}
                                        {stats.filtered_no_go > 0 && (
                                            <span className="px-2 py-1 bg-red-500/20 rounded text-red-300">
                                                🚫 {stats.filtered_no_go} restricted states
                                            </span>
                                        )}
                                        {stats.filtered_weight > 0 && (
                                            <span className="px-2 py-1 bg-zinc-700/50 rounded text-zinc-300">
                                                ⚖️ {stats.filtered_weight} overweight
                                            </span>
                                        )}
                                        {stats.filtered_length > 0 && (
                                            <span className="px-2 py-1 bg-zinc-700/50 rounded text-zinc-300">
                                                📏 {stats.filtered_length} too long
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
            
            {/* No Truck Selected State */}
            {!selectedTruck && !loadingTrucks && trucks.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
                    <CardContent className="p-12 text-center">
                        <Truck className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                        <h3 className="text-xl font-medium text-zinc-300 mb-2">Select a Truck</h3>
                        <p className="text-zinc-500 max-w-md mx-auto">
                            Tap a truck above to see loads ranked specifically for that truck's location, 
                            equipment type, and lane history.
                        </p>
                    </CardContent>
                </Card>
            )}
            
            {/* Loading Loads */}
            {loadingLoads && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-8 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" />
                        <span className="text-zinc-400">Ranking loads for {selectedTruckData?.name}...</span>
                    </CardContent>
                </Card>
            )}
            
            {/* Category Tabs & Load List */}
            {selectedTruck && data && !loadingLoads && (
                <>
                    {/* Category Tabs */}
                    <div className="flex gap-2">
                        {tabs.map(tab => (
                            <Button
                                key={tab.key}
                                variant={activeTab === tab.key ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setActiveTab(tab.key)}
                                className={activeTab === tab.key 
                                    ? (tab.key === 'priority' ? 'bg-blue-600' : 
                                       tab.key === 'consider' ? 'bg-amber-600' : 'bg-red-600')
                                    : 'border-zinc-700'
                                }
                                data-testid={`tab-${tab.key}`}
                            >
                                {tab.key === 'priority' && <Target className="w-4 h-4 mr-1" />}
                                {tab.key === 'consider' && <AlertTriangle className="w-4 h-4 mr-1" />}
                                {tab.key === 'avoid' && <TrendingDown className="w-4 h-4 mr-1" />}
                                {tab.label} ({tab.count})
                            </Button>
                        ))}
                    </div>
                    
                    {/* Load List */}
                    <div className="space-y-3">
                        {activeLoads.length === 0 ? (
                            <Card className="bg-zinc-900/50 border-zinc-800">
                                <CardContent className="p-8 text-center">
                                    <Info className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
                                    <p className="text-zinc-400">No loads in this category for {selectedTruckData?.name}</p>
                                </CardContent>
                            </Card>
                        ) : (
                            activeLoads.map(load => (
                                <PriorityLoadCard 
                                    key={load.load_id} 
                                    load={load}
                                    onStage={handleStage}
                                />
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// Export individual components for use elsewhere
export { PriorityLoadCard, ScoreBreakdown, getPriorityBadge, getDecisionBadge, TruckCard };
